/**
 * An allowlist HTML sanitiser for the Markdown preview.
 *
 * Nothing from the input is passed through as markup. The input is tokenised, and the
 * output is written from scratch: only the tags in `ALLOWED_TAGS`, only their listed
 * attributes, every value and every run of text escaped, every tag balanced. Script,
 * style, iframes, forms, SVG and MathML are dropped together with everything inside
 * them; comments and declarations are dropped; `on*` handlers, `style`, `class` (except
 * a `language-*` class on `code`) and `id` never survive; a link or image address must
 * be http, https, mailto or relative. Because the browser re-parses only what this
 * module wrote, there is no difference between what was checked and what runs.
 */

import {
  type HtmlAttribute,
  type HtmlToken,
  tokenizeHtml,
} from "@/lib/tool-kit/developer/html/tokenize";
import {
  decodeReference,
  escapeHtml,
  isSafeUrl,
} from "@/lib/tool-kit/developer/markdown/text";

export interface SanitizeOptions {
  /**
   * Shape the output for an on-page preview: links open in a new tab without a
   * referrer, and images load lazily without one.
   */
  preview?: boolean;
}

export const ALLOWED_TAGS = new Set([
  "a",
  "abbr",
  "address",
  "article",
  "aside",
  "b",
  "bdi",
  "bdo",
  "blockquote",
  "br",
  "caption",
  "cite",
  "code",
  "col",
  "colgroup",
  "dd",
  "del",
  "details",
  "dfn",
  "div",
  "dl",
  "dt",
  "em",
  "figcaption",
  "figure",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "i",
  "img",
  "ins",
  "kbd",
  "li",
  "main",
  "mark",
  "nav",
  "ol",
  "p",
  "pre",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "section",
  "small",
  "span",
  "strong",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "time",
  "tr",
  "u",
  "ul",
  "var",
  "wbr",
]);

/** Removed together with everything between their start and end tags. */
const DROP_WITH_CONTENT = new Set([
  "applet",
  "audio",
  "canvas",
  "datalist",
  "dialog",
  "embed",
  "frame",
  "frameset",
  "head",
  "iframe",
  "math",
  "noembed",
  "noframes",
  "noscript",
  "object",
  "option",
  "optgroup",
  "plaintext",
  "script",
  "select",
  "style",
  "svg",
  "template",
  "textarea",
  "title",
  "video",
  "xmp",
]);

const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "keygen",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const GLOBAL_ATTRIBUTES = new Set(["title", "lang", "dir"]);

const TAG_ATTRIBUTES: Record<string, ReadonlySet<string>> = {
  a: new Set(["href"]),
  img: new Set(["src", "alt", "width", "height"]),
  ol: new Set(["start", "reversed", "type"]),
  li: new Set(["value"]),
  th: new Set(["align", "colspan", "rowspan", "scope"]),
  td: new Set(["align", "colspan", "rowspan"]),
  col: new Set(["span"]),
  colgroup: new Set(["span"]),
  blockquote: new Set(["cite"]),
  q: new Set(["cite"]),
  del: new Set(["cite", "datetime"]),
  ins: new Set(["cite", "datetime"]),
  time: new Set(["datetime"]),
  details: new Set(["open"]),
  code: new Set(["class"]),
};

const URL_ATTRIBUTES = new Set(["href", "src", "cite"]);
const BOOLEAN_ATTRIBUTES = new Set(["reversed", "open"]);

const RE_INTEGER = /^\d{1,6}$/;
const RE_SIZE = /^\d{1,5}%?$/;
const RE_LANGUAGE_CLASS = /^language-[\w+#.-]{1,40}$/;
const RE_LANG = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{1,8})*$/;
const RE_REFERENCE_IN_VALUE =
  /&(?:#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});/g;
const RE_BARE_AMPERSAND =
  /&(?!(?:#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});)/g;
const RE_LT = /</g;
const RE_GT = />/g;

const ENUMERATED: Record<string, ReadonlySet<string>> = {
  align: new Set(["left", "center", "right", "justify"]),
  dir: new Set(["ltr", "rtl", "auto"]),
  scope: new Set(["row", "col", "rowgroup", "colgroup"]),
  type: new Set(["1", "a", "A", "i", "I"]),
};

const NUMERIC = new Set(["start", "value", "colspan", "rowspan", "span"]);

/** Text content: markup characters escaped, character references left for the browser. */
function escapeText(raw: string): string {
  return raw
    .replace(RE_BARE_AMPERSAND, "&amp;")
    .replace(RE_LT, "&lt;")
    .replace(RE_GT, "&gt;");
}

/** An attribute value as the browser would read it (the references this module knows). */
function decodeValue(raw: string): string {
  return raw.replace(
    RE_REFERENCE_IN_VALUE,
    (reference) => decodeReference(reference) ?? reference
  );
}

function valueAllowed(name: string, value: string): boolean {
  if (URL_ATTRIBUTES.has(name)) {
    return isSafeUrl(value);
  }
  if (NUMERIC.has(name)) {
    return RE_INTEGER.test(value.trim());
  }
  if (name === "width" || name === "height") {
    return RE_SIZE.test(value.trim());
  }
  if (name === "class") {
    return RE_LANGUAGE_CLASS.test(value.trim());
  }
  if (name === "lang") {
    return RE_LANG.test(value.trim());
  }
  const allowed = ENUMERATED[name];
  return allowed ? allowed.has(value.trim()) : true;
}

function attributeAllowed(tag: string, name: string): boolean {
  return (
    GLOBAL_ATTRIBUTES.has(name) || (TAG_ATTRIBUTES[tag]?.has(name) ?? false)
  );
}

/** The surviving attributes, serialised. */
function cleanAttributes(
  tag: string,
  attrs: HtmlAttribute[]
): Map<string, string> {
  const kept = new Map<string, string>();
  for (const attr of attrs) {
    if (kept.has(attr.name) || !attributeAllowed(tag, attr.name)) {
      continue;
    }
    if (BOOLEAN_ATTRIBUTES.has(attr.name)) {
      kept.set(attr.name, "");
      continue;
    }
    const value = decodeValue(attr.value ?? "");
    if (valueAllowed(attr.name, value)) {
      kept.set(attr.name, value);
    }
  }
  return kept;
}

function serializeStartTag(
  tag: string,
  attrs: Map<string, string>,
  options: SanitizeOptions
): string {
  let out = `<${tag}`;
  for (const [name, value] of attrs) {
    out += BOOLEAN_ATTRIBUTES.has(name)
      ? ` ${name}`
      : ` ${name}="${escapeHtml(value)}"`;
  }
  if (options.preview && tag === "a" && attrs.has("href")) {
    out += ' target="_blank" rel="noopener noreferrer nofollow"';
  }
  if (options.preview && tag === "img") {
    out += ' loading="lazy" referrerpolicy="no-referrer"';
  }
  return VOID_ELEMENTS.has(tag) ? `${out} />` : `${out}>`;
}

/** Start tags that close an element still open at the top of the stack. */
const IMPLIED_CLOSE: Record<string, readonly string[]> = {
  li: ["li"],
  dt: ["dt", "dd"],
  dd: ["dt", "dd"],
  tr: ["tr", "td", "th"],
  td: ["td", "th"],
  th: ["td", "th"],
  thead: ["thead", "tbody", "tfoot", "tr", "td", "th"],
  tbody: ["thead", "tbody", "tfoot", "tr", "td", "th"],
  tfoot: ["thead", "tbody", "tfoot", "tr", "td", "th"],
};

const CLOSES_PARAGRAPH = new Set([
  "address",
  "article",
  "aside",
  "blockquote",
  "details",
  "div",
  "dl",
  "figcaption",
  "figure",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "main",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "summary",
  "table",
  "ul",
]);

class SanitizeWriter {
  out = "";
  readonly stack: string[] = [];
  readonly options: SanitizeOptions;

  constructor(options: SanitizeOptions) {
    this.options = options;
  }

  closeTop(): void {
    const tag = this.stack.pop();
    if (tag) {
      this.out += `</${tag}>`;
    }
  }

  closeImplied(tag: string): void {
    const closes = IMPLIED_CLOSE[tag] ?? [];
    while (closes.includes(this.stack.at(-1) ?? "")) {
      this.closeTop();
    }
    if (CLOSES_PARAGRAPH.has(tag) && this.stack.at(-1) === "p") {
      this.closeTop();
    }
  }

  start(tag: string, attrs: HtmlAttribute[]): void {
    const kept = cleanAttributes(tag, attrs);
    if (tag === "img" && !kept.has("src")) {
      return;
    }
    this.closeImplied(tag);
    this.out += serializeStartTag(tag, kept, this.options);
    if (!VOID_ELEMENTS.has(tag)) {
      this.stack.push(tag);
    }
  }

  end(tag: string): void {
    if (VOID_ELEMENTS.has(tag) || !this.stack.includes(tag)) {
      return;
    }
    while (this.stack.length > 0) {
      const top = this.stack.at(-1);
      this.closeTop();
      if (top === tag) {
        return;
      }
    }
  }

  finish(): string {
    while (this.stack.length > 0) {
      this.closeTop();
    }
    return this.out;
  }
}

/** Whether a token starts an element to be removed along with its content. */
function startsDroppedElement(token: HtmlToken): string | null {
  if (token.kind !== "start" || !DROP_WITH_CONTENT.has(token.name)) {
    return null;
  }
  return token.selfClosing || VOID_ELEMENTS.has(token.name) ? "" : token.name;
}

interface Dropping {
  name: string;
  depth: number;
}

/** Track nesting inside a dropped element; null once its end tag closes it. */
function continueDropping(
  dropping: Dropping,
  token: HtmlToken
): Dropping | null {
  if (
    token.kind === "start" &&
    token.name === dropping.name &&
    !token.selfClosing
  ) {
    return { name: dropping.name, depth: dropping.depth + 1 };
  }
  if (token.kind === "end" && token.name === dropping.name) {
    return dropping.depth > 1
      ? { name: dropping.name, depth: dropping.depth - 1 }
      : null;
  }
  return dropping;
}

function writeToken(writer: SanitizeWriter, token: HtmlToken): void {
  if (token.kind === "text" || token.kind === "incomplete") {
    writer.out += escapeText(token.raw);
  } else if (token.kind === "start" && ALLOWED_TAGS.has(token.name)) {
    writer.start(token.name, token.attrs);
  } else if (token.kind === "end" && ALLOWED_TAGS.has(token.name)) {
    writer.end(token.name);
  }
}

export function sanitizeHtml(
  html: string,
  options: SanitizeOptions = {}
): string {
  const writer = new SanitizeWriter(options);
  let dropping: Dropping | null = null;
  for (const token of tokenizeHtml(html)) {
    if (dropping) {
      dropping = continueDropping(dropping, token);
      continue;
    }
    const dropped = startsDroppedElement(token);
    if (dropped === null) {
      writeToken(writer, token);
    } else if (dropped) {
      dropping = { name: dropped, depth: 1 };
    }
  }
  return writer.finish();
}
